/**
 * recommend.js — MOAT 1.0 Phase 5/13 · ONE recommendation engine.
 * Priority (user brief): 1 unfinished → 2 current objective → 3 diagnosed gap → 4 prerequisite weakness →
 * 5 due retrieval → 6 assessment need → 7 TTQS need → 8 English/reading need → 9 parent goal →
 * 10 application → 11 Live Quest → 12 game → 13 exploration → 14 creativity.
 * Wraps the existing deterministic engines (homeAdventure, nextAdventure) so Home, Learn hub, My World
 * and completion screens agree. Recommendation ≠ restriction: nothing is ever locked.
 */
import { getHomeAdventure } from "@/lib/homeAdventure";
import { primaryGoal, GOALS } from "@/lib/parentGoals";
import { nextObjective } from "@/lib/learningGraph";
import { memoryTags } from "@/lib/learningMemory";
import { loadFocus } from "@/lib/focusDay";
import { yearForAge } from "@/lib/canonical";
import { getActiveProfile } from "@/lib/profileStore";
import { getLevel } from "@/lib/learnerLevel";

const KIND_PRIORITY = { daily: 1, unfinished: 1, focus: 2, objective: 2, reteach: 3, weak: 4, review: 5, english: 6, ttqs: 7, reading: 8, goal: 9, assigned: 10, live: 11, game: 12, world: 13, create: 14 };
const REASON = { unfinished: "You were in the middle of this.", focus: "Part of your focus.", objective: "Your next step in this subject.", reteach: "This skill is still growing.", weak: "A tricky skill made stronger.", review: "A quick revisit locks it in.", english: "Your English next step.", ttqs: "Your times-tables facts need you.", goal: "Your grown-up's goal.", world: "Somewhere new to discover.", game: "A game you haven't played recently.", create: "Make something." };

export function getNextBest({ tier = "explorers" } = {}) {
  const home = getHomeAdventure({ tier });
  const prof = getActiveProfile();
  const year = getLevel()?.year || yearForAge(prof.age);
  const focus = loadFocus();
  const goal = primaryGoal();
  const memory = memoryTags();
  let today = { ...home.today, priority: KIND_PRIORITY[home.today.kind] || 12, reason: REASON[home.today.kind] || "" };

  // 2. current objective — an active Focus beats world/game fallbacks.
  if (today.priority >= 9 && focus) {
    const step = focus.subject ? nextObjective(focus.subject, focus.year, focus.curriculum) : null;
    today = { kind: "focus", kicker: "Your focus", title: step ? step.label : `Keep getting better at ${focus.subject}`, sub: `Practise · ${focus.subject}`, to: `/start/${focus.subject}?y=${focus.year}`, cta: "Continue", priority: 2, reason: REASON.focus };
  }
  // 9. parent goal — only when nothing higher-priority needs the child.
  if (today.priority >= 12 && goal) {
    const step = goal.subject ? nextObjective(goal.subject, year, "") : null;
    today = { kind: "goal", kicker: "Today's quest", title: step ? step.label : goal.label, sub: `${goal.e} ${goal.label}`, to: step ? `/learn-topics?s=${goal.subject}&y=${year}` : goal.to, cta: "Start", priority: 9, reason: REASON.goal };
  }
  // 10. application / 13. exploration / 14. creativity — one optional alternative that differs in kind.
  const alternatives = [
    memory.includes("prefers:world") && home.world.slug && { e: "🌍", label: `Discover ${home.world.country?.name}`, to: `/country/${home.world.slug}` },
    memory.includes("prefers:games") && { e: "🎮", label: home.game.label, to: home.game.to },
    { e: "🌍", label: "Explore", to: "/globe" }, { e: "🎮", label: "Play", to: "/play" }, { e: "🎨", label: "Create", to: "/coloring" }, { e: "📚", label: "Continue learning", to: "/learn-pathways" },
  ].filter(Boolean).filter((a) => a.to !== today.to);
  const seen = new Set(); const options = alternatives.filter((a) => (seen.has(a.label) ? false : seen.add(a.label))).slice(0, 4);
  return { ...home, today, alternative: options[0] || null, options: options.slice(1), goal, memory, year, goalsCatalog: GOALS };
}

/** After-activity loop: record evidence happened elsewhere; here we only answer "what next?" with one alternative. */
export function afterActivity({ tier, route }) {
  const n = getNextBest({ tier });
  const next = n.today.to === route ? (n.continue || n.path[0] || n.alternative) : n.today;
  return { next, alternative: n.options.find((o) => o.to !== next?.to) || null };
}
